import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtTL, fmtDate } from "@/lib/format";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Trash2 } from "lucide-react";
import { Badge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import ExportButton from "@/components/ExportButton";

const TYPE_LABELS = {
  order_debt: "Sipariş Borcu",
  payment_credit: "Ödeme",
  cancel: "İptal",
  return: "İade",
  adjustment: "Düzeltme",
};

export default function CariMovements() {
  const { user } = useAuth();
  const canDelete = ["admin", "accounting"].includes(user?.role);
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState(
    new URLSearchParams(window.location.search).get("company") || ""
  );
  const [movements, setMovements] = useState([]);

  useEffect(() => {
    api.get("/companies").then((r) => setCompanies(r.data));
  }, []);

  useEffect(() => {
    if (companyId) {
      api.get("/cari/movements", { params: { company_id: companyId } })
         .then((r) => setMovements(r.data));
    } else {
      setMovements([]);
    }
  }, [companyId]);

  const removePayment = async (paymentId) => {
    if (!window.confirm("Bu ödemeyi kalıcı olarak silmek istediğinize emin misiniz? Cari borç geri artırılacaktır.")) return;
    try {
      await api.delete(`/payments/${paymentId}`);
      toast.success("Ödeme silindi");
      // reload movements
      const { data } = await api.get("/cari/movements", { params: { company_id: companyId } });
      setMovements(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Silinemedi");
    }
  };

  const balance = movements.length > 0 ? movements[movements.length - 1].balance_after : 0;

  return (
    <div className="space-y-4">
      <div className="azd-card p-5">
        <label className="block text-xs font-semibold text-[#111827] mb-1.5">Firma Seçin</label>
        <Select value={companyId} onValueChange={setCompanyId}>
          <SelectTrigger data-testid="cari-mov-company" className="max-w-md">
            <SelectValue placeholder="Firma seçin" />
          </SelectTrigger>
          <SelectContent>
            {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {companyId && (
        <>
          <div className="flex justify-end">
            <ExportButton type="cari-movements" params={{ company_id: companyId }} label="Cari Hareketleri İndir" />
          </div>
          <div className="azd-card p-5">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280] font-bold">
              Güncel Bakiye
            </div>
            <div className={`text-3xl font-extrabold ${balance >= 0 ? "text-[#0B3676]" : "text-[#16A34A]"}`}>
              {fmtTL(Math.abs(balance))}
              <span className="text-sm font-normal text-[#6B7280] ml-2">
                ({balance >= 0 ? "Borçlu" : "Alacaklı"})
              </span>
            </div>
          </div>

          <div className="azd-card overflow-hidden">
            <div className="overflow-x-auto azd-scrollbar">
              <table className="w-full text-sm">
                <thead className="bg-[#F6F8FB] text-[11px] uppercase tracking-wider text-[#6B7280]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Tarih</th>
                    <th className="text-left px-4 py-3 font-semibold">Tip</th>
                    <th className="text-left px-4 py-3 font-semibold">Açıklama</th>
                    <th className="text-right px-4 py-3 font-semibold">Borç</th>
                    <th className="text-right px-4 py-3 font-semibold">Alacak</th>
                    <th className="text-right px-4 py-3 font-semibold">Bakiye</th>
                    <th className="text-right px-4 py-3 font-semibold"></th>
                  </tr>
                </thead>
                <tbody>
                  {movements.length === 0 ? (
                    <tr><td colSpan={7} className="p-8 text-center text-[#6B7280]">Hareket yok</td></tr>
                  ) : movements.map((m) => (
                    <tr key={m.id} className="border-t border-[#E5E7EB]">
                      <td className="px-4 py-3 text-xs text-[#6B7280]">{fmtDate(m.movement_date)}</td>
                      <td className="px-4 py-3">
                        <Badge variant={m.debit > 0 ? "error" : "success"}>
                          {TYPE_LABELS[m.movement_type] || m.movement_type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-[#6B7280]">{m.description}</td>
                      <td className="px-4 py-3 text-right text-[#DC2626] font-semibold">
                        {m.debit > 0 ? fmtTL(m.debit) : "-"}
                      </td>
                      <td className="px-4 py-3 text-right text-[#16A34A] font-semibold">
                        {m.credit > 0 ? fmtTL(m.credit) : "-"}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-[#08285A]">
                        {fmtTL(m.balance_after)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canDelete && m.payment_id && (
                          <button onClick={() => removePayment(m.payment_id)}
                                  data-testid={`delete-payment-${m.payment_id}`}
                                  className="p-1.5 text-[#6B7280] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded"
                                  title="Ödemeyi Sil">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
